import { BehaviorSubject } from 'rxjs';

import { Injectable } from '@angular/core';

import { uuidValueObject } from '@contexts/shared/domain';

import {
	type NotificationMessageMode,
	NotificationMessageModeOptions
} from '@components/atoms/notification-message/notification-message.component';

export type NotificationData = {
	id: string;
	title: string;
	message: string;
	mode: NotificationMessageMode;
	closable: boolean;
	status: NotificationStatus;
	expiration?: number;
};

export type NotificationConfiguration = {
	title: string;
	closable: boolean;
	expiration?: number;
};

export enum NotificationStatusOptions {
	CREATED = 'created',
	IN = 'in',
	OUT = 'out'
}

export type NotificationStatus = `${NotificationStatusOptions}`;

const DEFAULT_CONFIGURATION: Record<NotificationMessageMode, NotificationConfiguration> = {
	[NotificationMessageModeOptions.SUCCESS]: { title: 'Éxito', closable: true, expiration: 2000 },
	[NotificationMessageModeOptions.WARNING]: { title: 'Aviso', closable: true, expiration: 4000 },
	[NotificationMessageModeOptions.INFO]: {
		title: 'Información',
		closable: true,
		expiration: null
	},
	[NotificationMessageModeOptions.ERROR]: { title: 'Error', closable: true, expiration: 5000 }
};

@Injectable()
export class NotificationService {
	private notificationsSubject = new BehaviorSubject<NotificationData[]>([]);
	private configuration: Record<NotificationMessageMode, NotificationConfiguration> =
		DEFAULT_CONFIGURATION;

	notifications$ = this.notificationsSubject.asObservable();

	showNotification(
		message: string,
		mode: NotificationMessageMode,
		title?: string,
		closable?: boolean,
		expiration?: number | null
	) {
		const id = uuidValueObject().generate();
		const notification: NotificationData = {
			id,
			title: title ?? this.configuration[mode].title,
			message,
			mode,
			closable: closable !== undefined ? closable : this.configuration[mode].closable,
			expiration: expiration !== undefined ? expiration : this.configuration[mode].expiration,
			status: NotificationStatusOptions.CREATED
		};

		this.notificationsSubject.next([...this.notificationsSubject.value, notification]);
	}

	markAsIn(id: string) {
		this.notificationsSubject.value.map((n) => {
			if (n.id === id) {
				n.status = NotificationStatusOptions.IN;
			}

			return n;
		});
		this.notificationsSubject.next(this.notificationsSubject.value);
	}

	markAsOut(id: string) {
		this.notificationsSubject.value.map((n) => {
			if (n.id === id) {
				n.status = NotificationStatusOptions.OUT;
			}

			return n;
		});
		this.notificationsSubject.next(this.notificationsSubject.value);
	}

	removeNotification(id: string) {
		const updatedNotifications = this.notificationsSubject.value.filter((n) => n.id !== id);
		this.notificationsSubject.next(updatedNotifications);
	}
}
